
import { useState } from "react";
import { Plus, QrCode } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CreateQueueDialog } from "@/components/CreateQueueDialog";
import { Queue } from "@/types/queue";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction } from "@/components/ui/alert-dialog";

interface HeaderActionsProps {
  onCreateQueue: (queue: Omit<Queue, "id">) => Promise<void>;
}

export function HeaderActions({ onCreateQueue }: HeaderActionsProps) {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const { toast } = useToast();

  const handleCreateClick = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setErrorMessage("You must be logged in to create queues");
        setShowErrorDialog(true);
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select(`
          subscription_status,
          subscription_tier_id,
          subscription_tiers (
            queue_limit
          )
        `)
        .eq('id', user.id)
        .maybeSingle();

      if (profileError) throw profileError;

      // Allow queue creation for both 'active' and 'free' subscription status
      if (!profile || (profile.subscription_status !== 'active' && profile.subscription_status !== 'free')) {
        setErrorMessage("You need an active subscription to create queues");
        setShowErrorDialog(true);
        return;
      }

      const { count, error: countError } = await supabase
        .from('queues')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      if (countError) throw countError;

      const queueLimit = profile.subscription_tiers?.queue_limit || 0;
      
      if (count && count >= queueLimit) {
        setErrorMessage(`You've reached your queue limit (${queueLimit}). Please upgrade your subscription to create more queues.`);
        setShowErrorDialog(true);
        return;
      }

      setShowCreateDialog(true);
    } catch (error: any) {
      console.error('Error checking queue limits:', error);
      toast({
        title: "Error",
        description: "Failed to verify queue creation eligibility",
        variant: "destructive",
      });
    }
  };

  const handlePrintQRCodes = () => {
    window.open('/all-qr-codes', '_blank');
  };

  return (
    <div className="flex gap-2 w-full sm:w-auto">
      <Button onClick={handleCreateClick} className="gap-2 w-full sm:w-auto">
        <Plus className="w-4 h-4" />
        Create Queue
      </Button>
      <Button onClick={handlePrintQRCodes} variant="outline" className="gap-2 w-full sm:w-auto">
        <QrCode className="w-4 h-4" />
        Show All QR Codes
      </Button>

      <AlertDialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Subscription Notice</AlertDialogTitle>
            <AlertDialogDescription>{errorMessage}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowErrorDialog(false)}>
              Okay
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CreateQueueDialog 
        onCreateQueue={onCreateQueue}
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />
    </div>
  );
}
